<template>
  <v-container fluid class="pa-0">
    <v-row>
      <v-col class="pt-0 d-flex align-start" cols="12" sm="12" :md="showFilters ? 4 : 2" :class="{'mt-md-n10': !noMarginTop}">
        <v-card class="pb-2" elevation="0" outlined v-show="showFilters">
          <v-row class="mx-0 pt-2">
            <v-col class="py-0" cols="12" sm="12">
              <v-card-title class="pa-0 header-font py-2" style="font-size: 22px;">{{ $t('general.filters') }}</v-card-title>
              <MediaModeForm v-show="showFilters" v-if="(currentUser.clientType === 'Media' || currentUser.clientType === 'Driblab')" :fields="mediaModeFields"
                :mediaMode="mediaMode" @mediaModeChange="onMediaModeChange($event)"
                :mediaModeData="mediaModeData" @mediaModeDataChange="onMediaModeDataChange($event)" selectorMode></MediaModeForm>
            </v-col>
            <!--Visualization-->
            <v-col class="pb-1" cols="12" sm="12">
              <p class="my-0 pa-0 primary--text font-weight-bold body-1 mt-2">{{ $t('general.orientation') }}</p>
              <v-radio-group v-model="orientationModel" dense hide-details row class="mt-0" @change="xtMap++">
                <v-radio :color="darkMode ? 'secondary darken-2' : 'secondary'" value="horizontal">
                  <template v-slot:label>
                    <span class="body-1 primary--text">{{ $t('general.horizontal') }}</span>
                  </template>
                </v-radio>
                <v-radio :color="darkMode ? 'secondary darken-2' : 'secondary'" value="vertical">
                  <template v-slot:label>
                    <span class="body-1 primary--text">{{ $t('general.vertical') }}</span>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-col>
            <v-col class="pb-1" cols="12" sm="12">
              <p class="my-0 pa-0 primary--text font-weight-bold body-1">{{ $t('game.gameThreat.typeOfPlay') }}</p>
            </v-col>
            <v-col cols="12" class="d-flex flex-wrap pt-0 pb-1">
              <v-checkbox
                v-for="(typesPlay, index) in typeOfPlay"
                :key="typesPlay"
                v-model="typeOfPlayModel"
                :label="typesPlay"
                color="secondary"
                :value="typesPlay"
                hide-details
                class="px-1 py-0 ma-0"
                @change="updateShots()"
              >
                <template v-slot:label>
                  <span class="body-1 primary--text">{{ typesPlay }}</span>
                </template>
              </v-checkbox>
            </v-col>
            <v-col class="py-1" cols="12" sm="12">
              <p class="my-0 pa-0 primary--text font-weight-bold body-1 mt-2">{{ $t('game.gameThreat.actionEnding') }}</p>
            </v-col>
            <v-col cols="12" class="d-flex flex-wrap pt-0 pb-1">
              <v-checkbox
                v-for="(actionEnding, index) in actionEndings"
                :key="actionEnding"
                v-model="actionEndingsModel"
                :label="$t(`game.gameThreat.${actionEnding}`)"
                color="secondary"
                :value="actionEnding"
                hide-details
                class="px-1 py-0 ma-0"
                @change="updateShots()"
              >
                <template v-slot:label>
                  <span class="body-1 primary--text">{{ $t(`game.gameThreat.${actionEnding}`) }}</span>
                </template>
              </v-checkbox>
            </v-col>
            <v-col class="py-1" cols="12" sm="12" v-if="showShotOptions">
              <p class="my-0 pa-0 primary--text font-weight-bold body-1 mt-2">{{ $t('general.period') }}</p>
            </v-col>
            <v-col cols="12" class="d-flex flex-wrap py-0" v-if="showShotOptions">
              <v-checkbox
                v-model="periods"
                :label="$t('general.firstHalf')"
                color="secondary"
                value="period1"
                hide-details
                class="px-1 py-0 ma-0"
                @change="updateShots()"
              >
                <template v-slot:label>
                  <span class="body-1 primary--text">{{ $t('general.firstHalf') }}</span>
                </template>
              </v-checkbox>
              <v-checkbox
                v-model="periods"
                :label="$t('general.secondHalf')"
                color="secondary"
                value="period2"
                hide-details
                class="px-1 py-0 ma-0"
                @change="updateShots()"
              >
                <template v-slot:label>
                  <span class="body-1 primary--text">{{ $t('general.secondHalf') }}</span>
                </template>
              </v-checkbox>
            </v-col>
            <v-col cols="12" md="6" class="py-0" v-if="showShotOptions && hasFirstOT">
              <v-checkbox
                v-model="periods"
                :label="$t('general.firstPeriodOT')"
                color="secondary"
                value="period3"
                hide-details
                class="ma-0 py-0"
                @change="updateShots()"
              >
                <template v-slot:label>
                  <span class="body-1 primary--text">{{ $t('general.firstPeriodOT') }}</span>
                </template>
              </v-checkbox>
            </v-col>
            <v-col cols="12" md="6" class="py-0" v-if="showShotOptions && hasSecondOT">
              <v-checkbox
                v-model="periods"
                :label="$t('general.secondPeriodOT')"
                color="secondary"
                value="period4"
                hide-details
                class="ma-0 py-0"
                @change="updateShots()"
              >
                <template v-slot:label>
                  <span class="body-1 primary--text">{{ $t('general.secondPeriodOT') }}</span>
                </template>
              </v-checkbox>
            </v-col>
            <v-col class="d-flex pt-1 pb-2 my-2" cols="12" sm="12" v-if="showShotOptions">
              <v-range-slider
                thumb-label
                v-model="minutes"
                :max="minutesMax"
                :min="minutesMin"
                step="1"
                hide-details
                :color="`secondary ${$vuetify.theme.dark ? 'lighten-3' : ''}`"
                class="align-center py-0"
                @input="updateShots"
              >
                <template v-slot:label>
                  <div class="d-flex flex-column">
                    <span class="primary--text font-weight-bold body-1">{{ $t('finishingMap.minutes') }}</span>
                    <span class="body-2 subheader-font monospace-font">{{ `(${minutesMin} - ${minutesMax})` }}</span>
                  </div>
                </template>
              </v-range-slider>
            </v-col>
            <v-col class="d-flex pt-1 pb-2 mb-5" cols="12" sm="12">
              <v-range-slider
                :label="$t('game.gameThreat.threat')"
                thumb-label
                v-model="threat"
                :max="threatMax"
                :min="threatMin"
                step="0.01"
                hide-details
                :color="`secondary ${$vuetify.theme.dark ? 'lighten-3' : ''}`"
                class="align-center py-0"
                @input="updateShots"
              >
                <template v-slot:label>
                  <div class="d-flex flex-column">
                    <span class="primary--text font-weight-bold body-1">{{ $t('game.gameThreat.threat') }}</span>
                    <span class="body-2 subheader-font monospace-font">{{ `(${threatMin} - ${threatMax})` }}</span>
                  </div>
                </template>
              </v-range-slider>
            </v-col>
            <v-col class="d-flex justify-end pt-3 pb-1 px-2" cols="12" >
              <v-divider class="w-100"></v-divider>
            </v-col>
            <v-col class="d-flex justify-end pt-2 pb-1 px-2" cols="12" >
              <v-btn
                color="secondary" class="elevation-0 monospace-font white--text px-3 mx-1" rounded small
                :x-small="!$vuetify.breakpoint.xsOnly"
                v-on:click.native="saveAsPNG()"
              >
                <v-icon small class="mr-1">
                  file_download</v-icon
                >
                <span class="">PNG</span>
              </v-btn>
              <v-btn
                color="secondary" class="elevation-0 monospace-font white--text px-3 mx-1" rounded small
                :x-small="!$vuetify.breakpoint.xsOnly"
                v-on:click.native="saveAsSVG()"
              >
                <v-icon small
                  >file_download</v-icon
                >
                <span>SVG</span>
              </v-btn>
              <v-btn
                color="secondary" class="elevation-0 monospace-font white--text px-3 mx-1" rounded small
                :x-small="!$vuetify.breakpoint.xsOnly"
                :loading="loadingExport"
                  :disabled="!chartSize"
                v-on:click.native="onGenerateIFrameClick()"
              >
                <v-icon small class="mr-1"
                  >share</v-icon
                >
                <span>Embed</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
        <div class="flex-shrink-0 ml-2 align-self-start">
          <slot name="filters-toggle"></slot>
        </div>
      </v-col>
      <v-col cols="12" sm="12" :md="showFilters ? 8 : 10" id="chart" ref="chart" class="pt-0 d-flex justify-center'">
        <div class="d-flex flex-column align-center w-100">
          <GameAvatarMedia v-if="avatarSize && mediaMode && game" 
            :key="`avatar-${orientationModel}`"
            :width="avatarSize.width" 
            :height="avatarSize.height" 
            :id="`${gameId}-${chartId}`" 
            :game="game"
            :margin="margin" 
            :titleLeft="isMobile ? game.homeTeamAlias : game.homeTeamName" 
            :titleRight="isMobile ? game.awayTeamAlias : game.awayTeamName"
            :leftColor="isWhite(mediaModeData.leftFillColor) ? mediaModeData.leftBorderColor : mediaModeData.leftFillColor"
            :rightColor="isWhite(mediaModeData.rightFillColor) ? mediaModeData.rightBorderColor : mediaModeData.rightFillColor"
            ref="gameAvatarMediaRef" :bgColor="mediaModeData.bgColor" :textColor="mediaModeData.textColor"
            type="gameThreat" :lineDistance="mediaModeData.lineLength" :avatarSize="mediaModeData.avatarSize" :lineWeight="mediaModeData.lineWeight"
            :titleFontSize="mediaModeData.titleFontSize" :titleFontWeight="mediaModeData.titleFontWeight"
            :subtitle="mediaModeData.subtitle" :subtitleFontSize="mediaModeData.subtitleFontSize" :subtitleFontWeight="mediaModeData.subtitleFontWeight"
            :subtitle2="mediaModeData.subtitle2" :subtitle2FontSize="mediaModeData.subtitle2FontSize" :subtitle2FontWeight="mediaModeData.subtitle2FontWeight"
            :isVertical="orientationModel === 'vertical'">
          </GameAvatarMedia>
          <XTChart
            v-if="size != 0 && game !== null && filteredShots != null && filteredShots.length > 0"
            :size="size"
            :type="type"
            :key="xtMap"
            :game="game"
            :plays="filteredShots"
            :minXT="threat[0]"
            :maxXT="threat[1]"
            :language="currentUserLanguage"
            :typeOfPlay="typeOfPlayModel"
            :homeFillColor="mediaModeData.leftFillColor"
            :awayFillColor="mediaModeData.rightFillColor"
            :homeBorderColor="mediaModeData.leftBorderColor"
            :awayBorderColor="mediaModeData.rightBorderColor"
            :homeTeamName="mediaModeData.titleLeft"
            :awayTeamName="mediaModeData.titleRight"
            :mediaMode="mediaMode"
            :id="`${gameId}-${chartId}`"
            :filterShotTypeText="gameFilterTypeOfPlayText"
            :filterTimeText="gameFilterTimeText"
            :filterActionEndingText="gameFilterActionEndingText"
            :orientation="orientationModel"
            @sizeChange="onChartSizeChange($event)"
            @loaded="onLoaded($event)"
          />
          <DriblabSocialMedia v-if="game && avatarSize && size && mediaMode && mediaModeData.socialMedia"  :width="avatarSize.width" :height="50" :id="`${gameId}-${chartId}`"
            :margin="margin" ref="driblabSocialMediaRef" :bgColor="mediaModeData.bgColor" :textColor="mediaModeData.textColor"
            :fontSize="mediaModeData.socialMediaFontSize" :fontWeight="mediaModeData.socialMediaFontWeight"
            :logoCfg="mediaModeData.logo" :sponsor="mediaModeData.sponsor.dataUrl" type="gameThreat"
            :isVertical="orientationModel === 'vertical'"
            :isGame="true">
          </DriblabSocialMedia>
        </div>
      </v-col>
    </v-row>
    <ChartExportLinksModal ref="chartExportLinksModalRef" :title="`${game.homeTeamName} ${game.homeTeamScore} - ${game.awayTeamScore} ${game.awayTeamName}`" v-if="game"
    :subtitle="$t('playerDetail.threatMap')"></ChartExportLinksModal>
  </v-container>
</template>

<script>
import * as d3 from "d3";

import GameService from '../../services/game/game.service';
import XTChart from '../base/XTChart';
import MediaModeForm from '../base/MediaModeForm';
import DriblabSocialMedia from '../base/DriblabSocialMedia';
import GameAvatarMedia from './GameAvatarMedia';
import { getNeutralColors,userHasRole } from '../../utils/functions';
import { exportSvg,downloadPng,copyAndAppendElement } from '../../utils/exportToPNG';
import ChartExportLinksModal from '../base/ChartExportLinksModal';
import { v4 } from 'uuid';
import UserChartService from '../../services/user-chart.service';
import DocumentService from '../../services/document.service';
import UserService from '../../services/user.service';

const NODE_ENV = process.env.NODE_ENV;
const PROTOCOL = process.env.PROTOCOL || 'http://';
const END_POINT_URL = process.env.END_POINT_URL || window.location.hostname;
const END_POINT_PORT = process.env.END_POINT_PORT || '8081';

const API_URL = NODE_ENV === 'production' ? 'https://pro.driblab.com/embed-chart/' : PROTOCOL + END_POINT_URL + ':' + END_POINT_PORT + '/embed-chart/'


export default {
  name: 'GameXTMap',
  components: {
    XTChart,
    MediaModeForm,
    GameAvatarMedia,
    DriblabSocialMedia,
    ChartExportLinksModal
  },
  props:{
    noMarginTop: { type: Boolean, default: false },
    gameId:{
      type:Number,
      required: true
    },
    chartId:{
      type:Number,
      default: 0
    },
    language: {
      type: String,
      required: false
    },
    showFilters: {
      type: Boolean,
      default: true
    }
  },
  watch: {
    "mediaMode": function(newVal, oldVal) {
      if(!this.mediaMode){
        this.mediaModeData = {leftBorderColor:"#3366FF",rightBorderColor:"#3366FF",bgColor:"#ffffff0",socialMedia:true,textColor:"#000000",logo:"color",
                      titleFontSize:"18",titleFontWeight:"900",subtitleFontSize:"14",subtitleFontWeight:"500",lineWeight:"2",lineLength:"180",avatarSize:"64"
                      ,subtitle2FontSize:"12",subtitle2FontWeight:"300",sponsor:{}}
        this.mediaModeData.subtitle = this.game.leagueName;
        this.mediaModeData.subtitle2 = this.$t("game.header.gameThreat");
        this.mediaModeData.titleLeft = this.game.homeTeamName;
        this.mediaModeData.titleRight = this.game.awayTeamName;
        this.mediaModeData.leftFillColor = this.game.homeTeamGameColor ? this.game.homeTeamGameColor.replace("0x","#") : getNeutralColors('home')[1];
        this.mediaModeData.rightFillColor = this.game.awayTeamGameColor ? this.game.awayTeamGameColor.replace("0x","#") : getNeutralColors('away')[1];
        this.mediaModeData.leftBorderColor = this.game.homeTeamGameSecondColor ? this.game.homeTeamGameSecondColor.replace("0x","#") : getNeutralColors('home')[0];
        this.mediaModeData.rightBorderColor = this.game.awayTeamGameSecondColor ? this.game.awayTeamGameSecondColor.replace("0x","#") : getNeutralColors('away')[0];
        this.mediaModeData.sponsor.src = require(`@/assets/img/driblab_normal_color.png`);
        this.mediaModeData.sponsor.fallBackSrc = require(`@/assets/img/driblab_normal_color.png`);
      }
      this.mediaModeData.bgColor = this.mediaMode ? '#ffffff' : '#ffffff0';
      this.onWindowResize(null,false)
    },
    showFilters() {
      this.$nextTick(() => {
        this.size = this.$refs.chart.clientWidth * this.sizeMultiplier;
        this.onWindowResize(null, true);
      });
    },
    orientationModel() {
      this.$nextTick(() => {
        this.onWindowResize(null, true);
      });
    },
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    currentUserLanguage() {
      return this.language ? this.language : this.$store.getters["auth/language"];
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown || this.$vuetify.breakpoint.mobile;
    },
    hasFirstOT(){
      return _.some(this.shots,(s) => s.periodId === 3)
    },
    hasSecondOT(){
      return _.some(this.shots,(s) => s.periodId === 4)
    },
    avatarSize() {
      if (this.orientationModel === 'horizontal') {
        return {
          width: this.size,
          height: this.size / 6.77
        }
      }
      return {
        width: this.size / 1.49,
        height: this.size / 5
      }
    },
    lastSearchId() {
      return this.$store.state.auth.lastSearch;
    },
    darkMode(){
      return this.currentUser.darkMode || this.$vuetify.theme.dark;
    },
    gameFilterTypeOfPlayText() {
      if (this.typeOfPlayModel.length === 2) return '';
      if (this.typeOfPlayModel.includes('Set Piece')) return this.$t('game.gameThreat.setPiece');
      return this.$t('game.gameThreat.regularPlay');
    },
    gameFilterTimeText() {
      if (this.periods.length === 4 && this.minutes[0] === 0 && this.minutes[1] === 90) return '';
      let parts = [];
      if (this.periods.length < 4) {
        parts.push(this.periods.map(p => this.$t(`game.gameThreat.${p}`)).join(', '));
      }
      if (this.minutes[0] > 0 || this.minutes[1] < 90) {
        parts.push(`${this.minutes[0]}' - ${this.minutes[1]}'`);
      }
      return parts.join(' · ');
    },
    gameFilterActionEndingText() {
      if (this.actionEndingsModel.length === this.actionEndings.length || this.actionEndingsModel.length === 0) return '';
      const labels = this.actionEndingsModel.map(ae => this.$t(`game.gameThreat.${ae}`));
      if (labels.length <= 2) return labels.join(', ');
      return labels.slice(0, 2).join(', ') + ` (+${labels.length - 2})`;
    },
    sizeMultiplier () {
      if (this.orientationModel === 'vertical' && this.$vuetify.breakpoint.lgAndDown && !this.mediaMode) {
        return 1.25;
      } else return 0.925;
    }
  },
  data() {
    return {
      shots: [],
      filteredShots: [],
      size: 0,
      margin: null,
      type: 'game',
      game: null,
      xtMap: 0,
      showShotOptions: this.$vuetify.breakpoint.smAndDown ? false : true,
      minutes: [0, 90],
      periods: ['period1','period2', 'period3', 'period4'],
      minutesMax: 90,
      minutesMin: 0,
      threat: [0.05, 1],
      threatMax: 1,
      threatMin: 0.02,
      actionEndings: ['pass', 'shot', 'foul', 'other'],
      actionEndingsModel: ['pass', 'shot', 'foul', 'other'],
      typeOfPlay: ['Regular Play', 'Set Piece'],
      typeOfPlayModel: ['Regular Play', 'Set Piece'],
      mediaMode: false,
      mediaModeData: {leftBorderColor:"#3366FF",rightBorderColor:"#3366FF",bgColor:"#ffffff0",socialMedia:true,textColor:"#000000",logo:"color",
                      titleFontSize:"18",titleFontWeight:"900",subtitleFontSize:"14",subtitleFontWeight:"500",lineWeight:"2",lineLength:"180",avatarSize:"64"
                      ,subtitle2FontSize:"12",subtitle2FontWeight:"300",sponsor:{}},
      mediaModeFields: [
        {type:"color",label:"mediaMode.borderColorX",key:"leftBorderColor",labelParams:[1],home:true},
        {type:"color",label:"mediaMode.borderColorX",key:"rightBorderColor",labelParams:[2],away:true},
        {type:"color",label:"mediaMode.fillColorX",key:"leftFillColor",labelParams:[1],home:true},
        {type:"color",label:"mediaMode.fillColorX",key:"rightFillColor",labelParams:[2],away:true},
        {type:"text",label:"mediaMode.titleX",key:"titleLeft",labelParams:[1],home:true},
        {type:"text",label:"mediaMode.titleX",key:"titleRight",labelParams:[2],away:true},
        {type:"number",label:"mediaMode.titleFontSize",key:"titleFontSize",force:true},
        {type:"radio",label:"mediaMode.titleFontWeight",key:"titleFontWeight"
          ,options:[{value:"100",label:"100"},{value:"300",label:"300"},{value:"400",label:"400"},{value:"500",label:"500"},{value:"700",label:"700"},{value:"900",label:"900"}],force:true},
        {type:"text",label:"mediaMode.subtitle",key:"subtitle"},
        {type:"number",label:"mediaMode.subtitleFontSize",key:"subtitleFontSize"},
        {type:"radio",label:"mediaMode.subtitleFontWeight",key:"subtitleFontWeight"
          ,options:[{value:"100",label:"100"},{value:"300",label:"300"},{value:"400",label:"400"},{value:"500",label:"500"},{value:"700",label:"700"},{value:"900",label:"900"}]},
        {type:"text",label:"mediaMode.subtitleX",key:"subtitle2",labelParams:[2]},
        {type:"number",label:"mediaMode.subtitleFontSizeX",key:"subtitle2FontSize",labelParams:[2]},
        {type:"radio",label:"mediaMode.subtitleFontWeightX",key:"subtitle2FontWeight",labelParams:[2]
          ,options:[{value:"100",label:"100"},{value:"300",label:"300"},{value:"400",label:"400"},{value:"500",label:"500"},{value:"700",label:"700"},{value:"900",label:"900"}]},
        {type:"color",label:"mediaMode.textColor",key:"textColor"},
        {type:"color",label:"mediaMode.bgColor",key:"bgColor"},
        {type:"number",label:"mediaMode.avatarLineLength",key:"lineLength",force:true},
        {type:"number",label:"mediaMode.avatarLineWeight",key:"lineWeight",force:true},
        {type:"number",label:"mediaMode.avatarSize",key:"avatarSize",force:true},
        {type:"switch",label:"mediaMode.socialMedia",key:"socialMedia"},
        {type:"number",label:"mediaMode.socialMediaFontSize",key:"socialMediaFontSize",force:true},
        {type:"radio",label:"mediaMode.socialMediaFontWeight",key:"socialMediaFontWeight"
          ,options:[{value:"100",label:"100"},{value:"300",label:"300"},{value:"400",label:"400"},{value:"500",label:"500"},{value:"700",label:"700"},{value:"900",label:"900"}],force:true},
        {type:"image",label:"mediaMode.sponsor",key:"sponsor",force:true,aspectRatio:16/9,imageRestriction:'none',resizable:true,stencilSize:{width:300,height:300}}
      ],
      chartSize: null,
      loadingExport: false,
      orientationModel: 'horizontal',
      isEmbed: false
    };
  },
  mounted: function() {
    this.mediaMode = (this.currentUser.mediaMode || this.currentUser.clientType === 'Media');
    this.size = this.$refs.chart.clientWidth * this.sizeMultiplier; // minus padding
  },
  created() {
    window.addEventListener("resize", this.onWindowResize);
    return Promise.all([GameService.getXTOfGame(this.gameId), GameService.getGame(this.gameId)]).then(([passesData, gameData]) => {
      const uniqueType = [...new Set(passesData.data.map(item => item.type))];
      const uniqueStrategy = [...new Set(passesData.data.map(item => item.strategy))];
        this.shots = passesData.data;
        this.game = gameData.data;

        this.mediaModeData.subtitle = this.game.leagueName;
        this.mediaModeData.subtitle2 = this.$t("game.header.gameThreat");
        this.mediaModeData.titleLeft = this.game.homeTeamName;
        this.mediaModeData.titleRight = this.game.awayTeamName;
        this.mediaModeData.leftFillColor = this.game.homeTeamGameColor ? this.game.homeTeamGameColor.replace("0x","#") : getNeutralColors('home')[1];
        this.mediaModeData.rightFillColor = this.game.awayTeamGameColor ? this.game.awayTeamGameColor.replace("0x","#") : getNeutralColors('away')[1];
        this.mediaModeData.leftBorderColor = this.game.homeTeamGameSecondColor ? this.game.homeTeamGameSecondColor.replace("0x","#") : getNeutralColors('home')[0];
        this.mediaModeData.rightBorderColor = this.game.awayTeamGameSecondColor ? this.game.awayTeamGameSecondColor.replace("0x","#") : getNeutralColors('away')[0];
        this.mediaModeData.sponsor.src = require(`@/assets/img/driblab_normal_color.png`);
        this.mediaModeData.sponsor.fallBackSrc = require(`@/assets/img/driblab_normal_color.png`);
        this.mediaModeFields = _.map(this.mediaModeFields,(f) => {
          if(f.home){
            f.labelParams = [this.game.homeTeamName];
          }
          if(f.away){
            f.labelParams = [this.game.awayTeamName];
          }
          return f;
        })
        this.$forceUpdate();

        this.updateShots();
        UserService.putLastSearchRendered({id:this.lastSearchId});
      });
  },
  methods: {
    onWindowResize(e, force = false) {
      setTimeout(() => {
        if (this._isDestroyed || this._isBeingDestroyed || !this.$refs.chart) {
          return;
        }

        force = force || (e && e.type === "resize");
        if (this.size !== this.$refs.chart.clientWidth * 0.95) {
          this.size = this.$refs.chart.clientWidth * 0.95;
        }
        this.turnoversAndRecoveriesKey++;

        if (this.$refs.teamAvatarMediaRef) {
          this.$refs.teamAvatarMediaRef.printD3(force);
        }
        if (this.$refs.driblabSocialMediaRef) {
          this.$refs.driblabSocialMediaRef.printD3(force);
        }        
        if (this.$t) {
          this.$forceUpdate();
        }
      }, 100);
    },
    isSetPiece(type) {
        if (type === 'Corner taken') return "Set Piece";
        if (type === 'Free kick') return "Set Piece";
        if (type === 'Free kick taken') return "Set Piece";
        if (type === 'From corner') return "Set Piece";
        if (type === 'Indirect') return "Set Piece";
        if (type === 'Penalty') return "Set Piece";
        if (type === 'Set piece') return "Set Piece";
        if (type === 'Throw-in') return "Set Piece";
        if (type === 'Throw-in set piece') return "Set Piece";
        if (type === 'Goal Kick') return "Set Piece";

        if (type === 'Fast break') return "Regular Play";
        if (type === 'Keeper Throw') return "Regular Play";
        if (type === 'Regular play') return "Regular Play";
        if (type === 'Individual Play') return "Regular Play";

        
        return 'Regular Play';
    },
    updateShots() {
      var minutesMaxFilter = this.minutes[1] === 90 ? 150 : this.minutes[1];

      this.filteredShots = this.shots
      .filter(s => this.typeOfPlayModel.includes(this.isSetPiece(s.strategy)))
      .filter(s => _.some(this.periods,p => p.includes(s.periodId)))
      .filter(s => s.min >= this.minutes[0] && s.min < minutesMaxFilter)
      .filter(s => {
        if (!this.actionEndingsModel.includes("pass")) {
          return s.type !== "PASS";
        }
        return true;
      })
      .filter(s => {
        if (!this.actionEndingsModel.includes("foul")) {
          return s.type !== "FOUL";
        }
        return true;
      })
      .filter(s => {
        if (!this.actionEndingsModel.includes("shot")) {
          return !["GOAL", "SAVED SHOT", "MISSED SHOT", "SHOT ON POST", "CHANCE MISSED"].includes(s.type);
        }
        return true;
      })
      .filter(s => {
        if (!this.actionEndingsModel.includes("other")) {
          return ["GOAL", "SAVED SHOT", "MISSED SHOT", "SHOT ON POST", "CHANCE MISSED", "PASS", "FOUL"].includes(s.type);
        }
        return true;
      });

      this.$emit("filteredPassesData", _.orderBy(this.filteredShots,['xT'],['desc']));

      this.xtMap += 1;
    },
    saveAsPNG() {
      if(this.mediaMode){
         downloadPng(this.prepareSvgForMedia(),`Driblab_xTMap_${this.game.homeTeamName.replace(' ','_')}_${this.game.homeTeamScore}_${this.game.awayTeamScore}_${this.game.awayTeamName.replace(' ','_')}.png`);
      }else{
        let svg = d3.select(`#pitchXT${this.gameId}-${this.chartId}`)
        .select("svg").node()
        downloadPng(svg,`Driblab_xTMap_${this.game.homeTeamName.replace(' ','_')}_${this.game.homeTeamScore}_${this.game.awayTeamScore}_${this.game.awayTeamName.replace(' ','_')}.png`);
      }
    },
    saveAsSVG() {
      if(this.mediaMode){
        exportSvg(this.prepareSvgForMedia(), `Driblab_xTMap_${this.game.homeTeamName.replace(' ','_')}_${this.game.homeTeamScore}_${this.game.awayTeamScore}_${this.game.awayTeamName.replace(' ','_')}`, true, this.mediaModeData.bgColor);
      }
      else{
        let svg = d3.select(`#pitchXT${this.gameId}-${this.chartId}`).select("svg").node();
        exportSvg(svg, `Driblab_xTMap_${this.game.homeTeamName.replace(' ','_')}_${this.game.homeTeamScore}_${this.game.awayTeamScore}_${this.game.awayTeamName.replace(' ','_')}`);
      }
    },
    getElementToExport(){
      if(this.mediaMode){
        return this.prepareSvgForMedia();
      }else{
        let svg = d3.select(`#pitchXT${this.gameId}-${this.chartId}`)
          .select("svg").node()
       return svg;
      }
    },
    onMediaModeChange(event){
      this.mediaMode = event;
    },
    onMediaModeDataChange(event){
      this.mediaModeData = {...event};
      setTimeout(() => {this.onWindowResize(null,this.mediaModeData.force);},666)
    },
    prepareSvgForMedia() {
      let parent = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      let heightAccum = 0;
      
      const xOffset = 50; 
      const paddingRight = 50;
      const totalWidth = this.chartSize.width + xOffset + paddingRight;

      parent.style.background = this.mediaModeData.bgColor;

      // Header
      const avatarSvg = document.querySelector(`#gameAvatarMedia${this.gameId}-${this.chartId}SVG`);
      if (avatarSvg) {
        copyAndAppendElement(parent, avatarSvg, xOffset, heightAccum);
        heightAccum += this.avatarSize.height;
      }

      // Pitch
      const chartSvg = document.querySelector(`#pitchXT${this.gameId}-${this.chartId}SVG`);
      if (chartSvg) {
        copyAndAppendElement(parent, chartSvg, xOffset, heightAccum);
        heightAccum += this.chartSize.height;
      }

      // Footer
      if (this.mediaModeData.socialMedia) {
        const socialSvg = document.querySelector(`#driblabSocialMedia${this.gameId}-${this.chartId}SVG`);
        if (socialSvg) {
          copyAndAppendElement(parent, socialSvg, xOffset, heightAccum);
          heightAccum += 50;
        }
      }

      parent.setAttribute("width", totalWidth);
      parent.setAttribute("height", heightAccum);
      parent.setAttribute("viewBox", `0 0 ${totalWidth} ${heightAccum}`);

      return parent;
    },
    onChartSizeChange(event){
      this.chartSize = event;
    },
    onLoaded(event){
      //Emit when is loaded
      this.$emit("loaded", event);
    },
    isWhite(color){
      return color === "#FFF" || color === "#fff" || color === "#FFFFFF" || color === "#ffffff" || color === "white";
    },
    async onGenerateIFrameClick(){
      this.isEmbed = true;
      this.loadingExport = true;
      let elToExport = this.getElementToExport();
      let key = v4();
      key = `${key}.json`;
      let chartObject = {
        awsKey:key,
        tool: 'game-threat',
        category: 'game',
        componentId: this.gameId,
        width: elToExport.getAttribute("width"),
        height: this.mediaMode ? elToExport.getAttribute("width") * 0.9925 : elToExport.getAttribute("width") * 0.806
      }
      let postResponse = await UserChartService.postChart(chartObject)
      let chartEntity = postResponse.data;

      this.$refs.chartExportLinksModalRef.open(chartEntity);
      this.loadingExport = false;

      let urlResponse = await UserChartService.getUploadURL(chartEntity.id,key);
      let uploadUrl = urlResponse.data;
      var jsonse = JSON.stringify({
        margin: this.margin,
        size: Number(chartEntity.width),
        height: Number(chartEntity.height),
        plays: this.filteredShots,
        circlesAndBoxes: this.checkboxCircles,
        playerPosition:this.playerPosition,
        playerSeasonData:this.filteredSeasons,
        player:this.player,
        typeOfPlay:this.typeOfPlayModel,
        minXT:this.threat[0],
        maxXT:this.threat[1],
        language:this.currentUserLanguage,
        mediaModeData: this.mediaModeData,
        mediaMode: this.mediaMode,
        playerAvatarKey: 0,
        key: this.xtMap,
        socialMediaKey: 2,
        type: this.type,
        id: `${this.gameId}-${this.chartId}`,
        chartType:"xt-map",
        game: this.game,
        category:"game",
        filterTimeText: this.gameFilterTimeText,
        orientation: this.orientationModel,
        isEmbed: this.isEmbed,
        embedFactor: this.orientationModel === 'vertical' ? 0.67 : 1
      });

      var blob = new Blob([jsonse], {type: "application/json"});
      await DocumentService.upload(uploadUrl,blob);
      this.postSearchChart(chartEntity);
      this.isEmbed = false;
    },
    async postSearchChart(chart){
      const authorized = true;
      let metaInfo = null;
      let category = "game";
      let urlId = this.gameId;

      metaInfo = JSON.stringify({
        link: `${API_URL}${chart.id}/key/${chart.awsKey}/compId/${chart.componentId}/catId/${chart.categoryId}/tId/${chart.toolId}`
      });
      const tool = 'embed';
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const browser = navigator.userAgent;
      let searchResponse = await UserService.postUserSearch(category, tool, urlId, authorized, this.$route.path, timeZone, browser, metaInfo);
      UserService.putLastSearchRendered({category,tool,id:searchResponse.data})
    },
    userHasRole(role) {
      return userHasRole(this.currentUser, role);
    },
  },
  destroyed() {
    d3.selectAll('.tooltip').remove();
    window.removeEventListener("resize", this.onWindowResize);
  },
};
</script>

<style scoped>
  body > div.tooltip {
    font-family: 'NeueMontreal';
    pointer-events: none;
    position: absolute;
    background: none repeat scroll 0 0 #ffffff;
    padding: 5px 10px 5px 10px;
    border-radius: 4px;
    text-align: center;
    display: none;
    line-height: 1.5;
    color: #081F2C;
    box-shadow: 0px 3px 9px rgba(0, 0, 0, .15);
  }

  body > div.tooltip:after {
    box-sizing: border-box;
    display: block;
    width: 100%;
    line-height: 2.8;
    color: #ffffff;
    content: "\25BC";
    text-align: center;
  }

</style>



// WEBPACK FOOTER //
// src/views/game/GameThreat.vue